import { cn } from "../lib/cn.js";

/**
 * 竖向步骤导航（product chrome）。组织搭建 wizard 左栏。
 * 视觉：已完成 = accent 实心 + check；当前 = accent 描边 + 序号；
 * 未达 = dim 描边 + 序号。仅「已完成」或「下一可达」步可点。
 */

export interface StepperItem {
  /** 1-based 步号 */
  step: number;
  title: string;
  subtitle?: string;
}

export function Stepper({
  items,
  activeStep,
  completedSteps,
  onSelect,
  className,
}: {
  items: ReadonlyArray<StepperItem>;
  activeStep: number;
  completedSteps: ReadonlyArray<number>;
  onSelect?: (step: number) => void;
  className?: string;
}): JSX.Element {
  const completed = new Set(completedSteps);
  const highestCompleted = completedSteps.reduce((m, s) => Math.max(m, s), 0);
  // 可达：已完成、当前、或「最高已完成 + 1」
  const reachable = (step: number): boolean =>
    completed.has(step) || step === activeStep || step <= highestCompleted + 1;

  return (
    <ol className={cn("flex flex-col", className)}>
      {items.map((item, idx) => {
        const isDone = completed.has(item.step);
        const isActive = item.step === activeStep;
        const isLast = idx === items.length - 1;
        const canClick = onSelect != null && reachable(item.step) && !isActive;
        return (
          <li key={item.step} className="relative flex gap-3">
            {/* 连接线 */}
            {!isLast ? (
              <span
                aria-hidden
                className={cn(
                  "absolute left-[13px] top-7 bottom-0 w-px",
                  isDone ? "bg-dosia-accent/40" : "bg-dosia-border",
                )}
              />
            ) : null}
            <button
              type="button"
              disabled={!canClick}
              onClick={canClick ? () => onSelect?.(item.step) : undefined}
              className={cn(
                "group flex flex-1 items-start gap-3 rounded-lg px-2 py-2 text-left transition-colors duration-150",
                canClick && "hover:bg-dosia-bg-lighter cursor-pointer",
                !canClick && !isActive && "cursor-default",
              )}
            >
              {/* 圆点 */}
              <span
                className={cn(
                  "relative z-10 mt-0.5 flex h-[27px] w-[27px] shrink-0 items-center justify-center rounded-full text-xs font-medium transition-colors duration-200",
                  isDone &&
                    "bg-dosia-accent text-dosia-bg border border-dosia-accent",
                  isActive &&
                    !isDone &&
                    "border-2 border-dosia-accent text-dosia-accent bg-dosia-bg-light",
                  !isActive &&
                    !isDone &&
                    "border border-dosia-border text-dosia-text-dim bg-dosia-bg-light",
                )}
              >
                {isDone ? (
                  <svg
                    viewBox="0 0 16 16"
                    className="h-3.5 w-3.5"
                    fill="none"
                    aria-hidden
                  >
                    <path
                      d="M3.5 8.5l3 3 6-6.5"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                ) : (
                  item.step
                )}
              </span>
              {/* 文案 */}
              <span className="flex flex-col gap-0.5 pt-0.5">
                <span
                  className={cn(
                    "text-sm leading-tight",
                    isActive
                      ? "text-dosia-text font-semibold"
                      : isDone
                        ? "text-dosia-text font-medium"
                        : "text-dosia-text-secondary",
                  )}
                >
                  {item.title}
                </span>
                {item.subtitle ? (
                  <span className="text-xs text-dosia-text-dim leading-snug">
                    {item.subtitle}
                  </span>
                ) : null}
              </span>
            </button>
          </li>
        );
      })}
    </ol>
  );
}
